import { useCallback, useEffect, useRef, useState } from "react";
import { entryIntentCopy, siteCopy } from "../../content";
import type { EntryIntent } from "./entryIntent";
import styles from "./EntryGate.module.css";

const copy = siteCopy.entry;

/** Decoration, not copy: the glyphs are part of the visual language. */
const intentSymbols: Readonly<Record<EntryIntent, string>> = {
  rest: "◌",
  direction: "↗",
  connection: "∞",
  surprise: "✦",
};

const intentOptions = (["rest", "direction", "connection", "surprise"] as const).map(
  (id) => ({ ...entryIntentCopy[id], symbol: intentSymbols[id] }),
);

const DOOR_ANIMATION_FALLBACK_MS = 1200;

export type EntryGateProps = Readonly<{
  /** Called once the doors have slid open, immediately for reduced motion. */
  onEnter: (intent: EntryIntent | null) => void;
  initialIntent?: EntryIntent | null;
  className?: string;
}>;

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function EntryGate({ onEnter, initialIntent = null, className }: EntryGateProps) {
  const [selectedIntent, setSelectedIntent] = useState<EntryIntent | null>(initialIntent);
  const [isOpening, setIsOpening] = useState(false);
  const hasCompletedRef = useRef(false);

  const completeEntry = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    onEnter(selectedIntent);
  }, [onEnter, selectedIntent]);

  useEffect(() => {
    if (!isOpening) {
      return undefined;
    }

    if (prefersReducedMotion()) {
      completeEntry();
      return undefined;
    }

    // Animation events may be cancelled when a tab is backgrounded. This is a
    // safety net, not the primary timing mechanism.
    const timeoutId = window.setTimeout(completeEntry, DOOR_ANIMATION_FALLBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [completeEntry, isOpening]);

  const openDoors = () => {
    if (isOpening || hasCompletedRef.current) {
      return;
    }

    if (prefersReducedMotion()) {
      hasCompletedRef.current = true;
      onEnter(selectedIntent);
      return;
    }

    setIsOpening(true);
  };

  const rootClassName = [styles.gate, className].filter(Boolean).join(" ");

  return (
    <section
      className={rootClassName}
      data-opening={isOpening ? "true" : "false"}
      aria-labelledby="entry-title"
    >
      <div className={styles.skyGlow} aria-hidden="true" />
      <div className={styles.shoreLine} aria-hidden="true" />

      <div className={styles.copy}>
        <p className={styles.eyebrow}>{copy.eyebrow}</p>
        <h1 id="entry-title">{copy.title}</h1>
        <p className={styles.intro}>{copy.intro}</p>

        <fieldset className={styles.intentions} disabled={isOpening}>
          <legend>{copy.intentLegend}</legend>
          <div className={styles.intentGrid}>
            {intentOptions.map((option) => (
              <label key={option.id} className={styles.intentOption}>
                <input
                  type="radio"
                  name="entry-intent"
                  value={option.id}
                  checked={selectedIntent === option.id}
                  onChange={() => setSelectedIntent(option.id)}
                />
                <span className={styles.intentCard}>
                  <span className={styles.intentSymbol} aria-hidden="true">
                    {option.symbol}
                  </span>
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.detail}</small>
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <button className={styles.enterButton} type="button" onClick={openDoors} disabled={isOpening}>
          <span>{isOpening ? copy.openingLabel : copy.enterLabel}</span>
          <span aria-hidden="true">→</span>
        </button>
        <p className={styles.privacyNote}>{copy.privacyNote}</p>
      </div>

      {/*
        The doors open on click too, because that is what everyone tries first.
        They are a second route to the button above rather than a control of
        their own, so they stay out of the tab order and out of the
        accessibility tree instead of announcing the same action twice.
      */}
      <button
        className={styles.doorway}
        type="button"
        data-doorway="true"
        aria-hidden="true"
        tabIndex={-1}
        onClick={openDoors}
      >
        <span className={styles.facade} />
        <span className={styles.roomGlimpse} />
        <span className={`${styles.door} ${styles.leftDoor}`} data-door="left" />
        <span
          className={`${styles.door} ${styles.rightDoor}`}
          data-door="right"
          onAnimationEnd={completeEntry}
        />
      </button>

      <p className={styles.liveRegion} aria-live="polite" aria-atomic="true">
        {isOpening ? copy.openingAnnouncement : ""}
      </p>
    </section>
  );
}
